import React from "react";
import ReactDOM from "react-dom/client";
// Before anything reads the stored theme: Use System on the web takes the computer's setting.
import { watchSystemTheme } from "./functions/systemTheme";
import "./i18n/i18n";
import "./index.css";
// SCO-2024: self-starting deploy-watcher (prod only) — checks /v2/version.json
// on foreground-return and silently reloads when a newer build is live
import "./functions/updateWatcher";
import App from "./App";
import NoNavLayout from "./Layouts/NoNavLayout";
import BlankLayout from "./Layouts/BlankLayout";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Text } from "./styles/Global";
import { store } from "./reducers/store";
import { setGlobal } from "./features/global/globalSlice";
import { Provider } from "react-redux";
import ReactGA from "react-ga4";
import loadable from "@loadable/component";
import UpgradeLayout from "./Layouts/UpgradeLayout";
import * as Sentry from "@sentry/react";
import AddToCalendar from "./components/AddToCalendar";
import { installEnableNotificationStub } from "./functions/nativeBridgeStubs";
import { syncEntitlementsFromUrl } from "./features/player/entitlements";
import EntitlementSync from "./features/player/EntitlementSync";
import NewLookWebPromo from "./pages/Global/newLook/NewLookWebPromo";
import { readStoredJson, writeStoredJson } from "./functions/safeStorage";
import { PROFILE_2027, followProfile2027Choice } from "./utils/profile2027Flag";
import { isOn as ui2027On, followUi2027Choices } from "./utils/ui2027Flag";
import { reportNewLookState } from "./utils/newLookState";
import { RatPageSkeleton } from "./pages/organizer/rat2027/ratSkeletons";
//import Organizer from "./pages/organizer/Organizer";
// Polyfill for globalThis (needed for old Android WebViews and Google Tag Manager)
if (typeof globalThis === "undefined") {
  window.globalThis = window;
}
// Polyfill for Object.hasOwn (Chrome 93+, Safari 15.4+) — missing on old Android WebViews
if (!Object.hasOwn) {
  Object.hasOwn = (obj, prop) => Object.prototype.hasOwnProperty.call(obj, prop);
}
// Polyfill for Object.fromEntries (ES2019 / Chrome 73+) — missing on old Android WebViews
if (!Object.fromEntries) {
  Object.fromEntries = (entries) => {
    const obj = {};
    for (const [k, v] of entries) obj[k] = v;
    return obj;
  };
}
// Polyfill for Array.prototype.flat / flatMap (ES2019 / Chrome 69+) — missing on
// old Android WebViews; AG Grid v32 calls them at runtime (SCO-2130). Defined
// non-enumerable so they don't leak into for...in over arrays.
if (!Array.prototype.flat) {
  Object.defineProperty(Array.prototype, "flat", {
    configurable: true,
    writable: true,
    enumerable: false,
    value: function (depth) {
      // spec semantics: holes are dropped at every flattened level, and a
      // depth of 0/negative/NaN copies without flattening
      const max = depth === undefined ? 1 : Math.floor(Number(depth)) || 0;
      const out = [];
      const walk = (arr, left) => {
        for (let i = 0; i < arr.length; i++) {
          if (!(i in arr)) continue;
          const val = arr[i];
          if (left > 0 && Array.isArray(val)) walk(val, left - 1);
          else out.push(val);
        }
      };
      walk(Object(this), max);
      return out;
    },
  });
}
if (!Array.prototype.flatMap) {
  Object.defineProperty(Array.prototype, "flatMap", {
    configurable: true,
    writable: true,
    enumerable: false,
    value: function (callback, thisArg) {
      return this.map(callback, thisArg).flat();
    },
  });
}
// SCO-2266: window.queueMicrotask (Chrome 71+ / WebView 71+) is missing on
// old Android WebViews (Chrome 64, Samsung SM-T377W). AG Grid v32's SSRM
// calls it at runtime and throws, which also leaves the grid mid-draw
// (SCOREHOLIO-REACT-167). Guarded so modern engines keep the native one.
if (typeof window.queueMicrotask !== "function") {
  window.queueMicrotask = (cb) =>
    Promise.resolve()
      .then(cb)
      .catch((e) =>
        setTimeout(() => {
          throw e;
        })
      );
}
// SCO-2060: the native shells call window.enableNotificationButton() whenever
// they feel like it — install the recording stub before anything renders so a
// call landing before the AdTracking/WelcomeModal chunk mounts can't throw.
installEnableNotificationStub();
// SCO-1870 chrome contract: migrated native screens pass headerHeight/
// bottomInset on the URL; the page owns its rest position via these vars
// (padding + fixed-nav top) so in-page chrome parks below the native header.
// Set once at boot — in-page navigate() drops query params but the vars stay.
{
  const bootParams = new URLSearchParams(window.location.search);
  // SCO-2495: paidPlayer / paidOrganizer from the URL → player slice before
  // first paint, so every webview (FAT, FAC, Profile, home-events…) gates on
  // what native just declared, not on whichever page last wrote localStorage.
  syncEntitlementsFromUrl(store.dispatch, window.location.search);
  // SCO-2710: follow a Profile 2027 choice saved in another webview (the native Settings sheet).
  followProfile2027Choice();
  // Seasons 2027 (SCO-2722): same follow for the ui2027 section switches.
  followUi2027Choices();
  // SCO-3066: the native home asks players to turn the new look on until this reports on.
  reportNewLookState();
  // A reload after in-page navigation boots without the params, so the shell's last values are kept
  // for this webview's session. A param on the URL always wins (0 included: legacy pages get 0).
  const chromeParam = (name) => {
    const raw = bootParams.get(name);
    try {
      if (raw === null) return parseFloat(sessionStorage.getItem(`sh-${name}`));
      sessionStorage.setItem(`sh-${name}`, raw);
    } catch (e) {
      // storage blocked: this load's params only
    }
    return parseFloat(raw);
  };
  const chromeTop = chromeParam("headerHeight");
  if (chromeTop > 0) {
    document.documentElement.style.setProperty("--sh-chrome-top", `${chromeTop}px`);
  }
  const chromeBottom = chromeParam("bottomInset");
  if (chromeBottom > 0) {
    document.documentElement.style.setProperty("--sh-chrome-bottom", `${chromeBottom}px`);
    // iOS reserves this strip as a scroll inset, so fixed sheets sit above it (Android's don't).
    // Flag off keeps main's sheet height (SCO-2710).
    if (PROFILE_2027 && window.webkit?.messageHandlers) {
      document.documentElement.style.setProperty("--sh-sheet-bottom", `${chromeBottom}px`);
    }
  }
  // Scroll-dodge signal for page-owned fixed bars (html.shNavDodge — the
  // groups tabs bar). Boot-level because BlankLayout pages never mount
  // App.jsx, where the nav's own dodge listener lives. Same rules: hide past
  // 12px scroll-down, show on any scroll-up, always shown near rest.
  if (chromeTop > 0) {
    let lastY = window.scrollY;
    window.addEventListener(
      "scroll",
      () => {
        const y = window.scrollY;
        const dy = y - lastY;
        lastY = y;
        if (y <= 12) {
          document.documentElement.classList.remove("shNavDodge");
        } else if (Math.abs(dy) >= 3) {
          document.documentElement.classList.toggle("shNavDodge", dy > 0);
        }
      },
      { passive: true },
    );
  }
}
// Auto-reload on ChunkLoadError (stale deployment / network blip).
// Manually captured in Sentry before reloading; ignoreErrors suppresses
// the duplicate auto-capture. Tag chunk_reload="reload_attempted" means
// we reloaded to fix it; "reload_failed" means it's still broken after reload.
// sessionStorage flag prevents an infinite reload loop; it's cleared 30s
// after a successful boot so a later deploy in the same session can also
// recover (SCO-2020).
function reloadOnceForChunk(error) {
  const alreadyReloaded = sessionStorage.getItem("chunkReloadAttempted");
  Sentry.captureException(error, {
    tags: {
      chunk_reload: alreadyReloaded ? "reload_failed" : "reload_attempted",
    },
  });
  if (!alreadyReloaded) {
    sessionStorage.setItem("chunkReloadAttempted", "1");
    window.location.reload();
  }
}
window.addEventListener("error", (e) => {
  // SCO-2020: when the SPA fallback serves index.html for a dead chunk URL,
  // the browser evaluates HTML as JS and throws SyntaxError ("Unexpected
  // token '<'") instead of ChunkLoadError. Same stale-deploy condition — but
  // only when the failing file is one of our chunks, so a genuine syntax
  // error in live code can't trigger reload churn.
  // SCO-2032: CSS chunks fail with "Loading CSS chunk N failed." (plain
  // Error, code CSS_CHUNK_LOAD_FAILED) — same stale deploy, so both matchers
  // cover js AND css.
  const staleChunkAsHtml =
    /\/static\/(js|css)\/.+\.chunk\.(js|css)/.test(e?.filename || "") &&
    (e?.error instanceof SyntaxError ||
      (e?.message || "").includes("Unexpected token"));
  if (/Loading (CSS )?chunk/.test(e?.message || "") || staleChunkAsHtml) {
    reloadOnceForChunk(
      e.error || new Error(`${e.message} (${e.filename || "unknown file"})`)
    );
  }
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = e?.reason?.message || "";
  if (
    e?.reason?.name === "ChunkLoadError" ||
    e?.reason?.code === "CSS_CHUNK_LOAD_FAILED" ||
    /Loading (CSS )?chunk/.test(msg)
  ) {
    reloadOnceForChunk(e.reason);
  }
});

// SCO-2208: Chrome/Google Translate re-parents text nodes into <font> wrappers,
// so a node React still tracks can have a different parent by commitDeletion time
// and native removeChild/insertBefore throw NotFoundError, crashing to the
// ErrorBoundary. Guard no-ops only the re-parent case (facebook/react#11538).
if (typeof Node === "function" && Node.prototype && !Node.prototype.__sco2208) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) return child;
    return originalRemoveChild.apply(this, arguments);
  };
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(newNode);
    }
    return originalInsertBefore.apply(this, arguments);
  };
  Object.defineProperty(Node.prototype, "__sco2208", { value: true, configurable: true });
}

// Initialize Sentry
Sentry.init({
  dsn: "https://d3e884265b5b912fe27940bbe9fcfe36@o4509696656867328.ingest.us.sentry.io/4510184466743296",
  sendDefaultPii: true,
  environment: process.env.NODE_ENV || "development",
  ignoreErrors: [
    /Connection to Indexed Database server lost/i,
    /IndexedDB.*not allowed/i, // private mode / storage disabled
    /A mutation operation was attempted on a database that did not allow mutations/i,
    // SCO-2072: suspending a webview/tab aborts Firebase's in-flight IndexedDB
    // transactions; the SDK-internal promise rejects with a bare
    // DOMException("AbortError"), rendered as exactly "AbortError: AbortError"
    // with no frames. Exact match only — real aborts (fetch cancellation,
    // navigator.share dismissal, media play()) carry distinct messages and
    // must keep reporting.
    /^AbortError: AbortError$/,
    // SCO-2121: Firebase holds IndexedDB open; when the browser/OS tears down
    // the origin's storage or suspends the webview mid-transaction, the
    // SDK-internal promise rejects with a bare DOMException, no app frames.
    // Anchored so genuine UnknownError/AbortError still report.
    /Connection is closing because of: Force close delete origin/i,
    /Database deleted by request of the user/i,
    /^AbortError: The connection was closed\.?$/,
    // SCO-2155: stream-overlay Firestore transport aborted when a link-preview
    // sandbox (Apple Mail/WebKit) tears the page down mid-load; SDK-internal
    // rejection, no app frames. Anchored so a genuine in-app abort still reports.
    /^AbortError: The operation was aborted\.?$/,
    // SCO-2141: three more Firebase SDK-internal IndexedDB rejections with
    // wordings the SCO-2072/2121 filters do not match (no app frames; the
    // browser/OS fails or tears down IndexedDB under Firebase's open
    // connection). Anchored so genuine app errors still report.
    /An internal error was encountered in the Indexed Database server/i,
    /^AbortError: The transaction was aborted/i,
    /^UnknownError: Connection is closing\.?$/,
    // SCO-2265: two more environmental Firebase IndexedDB wordings the
    // SCO-2072/2121/2141 filters miss — the device's disk/quota is full
    // (QuotaExceededError) or the origin's IndexedDB was cleared/rebuilt
    // underneath Firebase (InvalidStateError). No app frames; anchored on the
    // distinctive phrase so genuine app errors with these names still report.
    /Encountered full disk while opening backing store/i,
    // SCO-2872: Chromium "Internal error opening backing store" open failure —
    // corrupted/locked profile or another process holding Firebase's LevelDB
    // files (common on Windows). SDK-internal, no app frames; anchored on the
    // distinctive phrase so genuine app errors mentioning "backing store" report.
    /Internal error opening backing store for indexedDB/i,
    /InvalidStateError: Object store cannot be found in the database/i,
    // SCO-2682 (SCOREHOLIO-REACT-10S): Firebase's internal IndexedDB request runs
    // after its transaction completed/aborted when iOS WKWebView suspends/tears
    // down the page — SDK-internal rejection, no app frames (grep: zero hits in
    // src/). Specific phrase so a genuine in-app IndexedDB misuse still reports.
    /Attempt to get records from database without an in-progress transaction/i,
    // SCO-2871: another Firebase SDK-internal IndexedDB rejection — an Android
    // WebView suspends / the OS tears down the origin's storage under the open
    // connection. No app frames; anchored on the exact phrase so genuine app
    // InvalidStateErrors with other messages still report.
    /^InvalidStateError: Backend aborted error\.?$/,
    // SCO-2106: "Invalid call to runtime.sendMessage(). Tab not found." comes
    // only from third-party browser-extension / in-app-browser injected
    // content scripts (the WebExtensions runtime.sendMessage API) — never from
    // our bundle (grep: zero hits in src/). It reaches onunhandledrejection
    // often with no app frames, so the SCO-2049 beforeSend filter misses it.
    // Anchor on the runtime.sendMessage phrasing so a genuine app error that
    // merely mentions "Tab" is not swallowed.
    /Invalid call to runtime\.sendMessage\(\)/i,
    /Loading chunk/i, // handled manually in reloadOnceForChunk
    /Loading CSS chunk/i, // handled manually in reloadOnceForChunk (SCO-2032)
    /CSS_CHUNK_LOAD_FAILED/i, // handled manually in reloadOnceForChunk (SCO-2032)
    /ChunkLoadError/i, // handled manually in reloadOnceForChunk
  ],
  beforeSend(event, hint) {
    // SCO-2113: browser extensions / injected scripts dispatch a synthetic
    // unhandledrejection CustomEvent on window (isTrusted:false, non-Error
    // reason); Sentry's global handler captures the Event object as a rejection.
    // Real app rejections always carry an Error, so dropping non-Error DOM Event
    // reasons removes only this noise.
    const reason = hint?.originalException;
    if (reason instanceof Event) return null;
    // SCO-2209: same third-party-injected-noise family as SCO-2113/2106/2049 —
    // an email/link scanner or browser extension rejects with a bare string
    // "Object Not Found Matching Id:N, MethodName:update, ParamCount:4" (no
    // Error, no app frames; grep: zero hits in src/). Anchored on the injected
    // phrasing so a genuine app rejection that rejects with a string still reports.
    if (
      typeof reason === "string" &&
      /Object Not Found Matching Id:\d+, MethodName:/i.test(reason)
    )
      return null;
    // SCO-2049: Meta's iOS in-app browsers (Facebook/Instagram) inject a
    // script into every page that crashes on its own webkit bridge call on
    // page-hide (sendPageHideMessage → sendDataToNative). Drop error events
    // that are provably not ours: every stack frame comes from the document
    // itself (no .js file at all — injected inline script), or the frames
    // name Meta's injected functions. Keyed on frames, NOT message text —
    // our own bridge code also touches window.webkit.messageHandlers and
    // must keep reporting, and errors from real .js files (app bundle,
    // mapkit.js, …) always pass through.
    const frames = event?.exception?.values?.[0]?.stacktrace?.frames;
    if (frames?.length) {
      const metaInjected = frames.some(
        (f) =>
          f?.function === "sendDataToNative" ||
          f?.function === "sendPageHideMessage"
      );
      const allDocumentFrames = frames.every(
        (f) => !/\.js/.test(f?.filename || "")
      );
      if (metaInjected || allDocumentFrames) return null;
      // SCO-2344: Apple MapKit JS throws an internal unhandledrejection
      // ("null is not an object (evaluating 'a[0]')") during renderer-init
      // on iOS WKWebView — every frame is Apple's own mapkit.js / mk-csr.js,
      // zero app frames. We can't fix Apple's library, so drop events whose
      // frames are ALL from MapKit. Keyed on frame filenames, NOT message
      // text, so the app-framed MapKit crashes (SCO-2103/2073/2021/2152/2170,
      // which carry GridMap/app frames) still report.
      const allMapKitFrames = frames.every((f) =>
        /\/mk\/[\d.x]+\/mapkit\.js|\/ti\/csr\/[\d.x]+\/mk-csr\.js/.test(
          f?.filename || ""
        )
      );
      if (allMapKitFrames) return null;
      // SCO-2367: third-party browser-extension injected scripts (PayPal Honey,
      // etc.) throw internal rejections (UnavailableError) with every frame an
      // extension resource and no app frames. Drop events whose code frames are
      // ALL from a known extension origin; keyed on frame filenames, NOT message
      // text, so a mixed stack with even one real app .js frame still reports.
      const extensionFrame = (f) =>
        /\.safariextension\/|\.appex\/|^(?:safari-web-extension|chrome-extension|moz-extension):\/\//.test(
          f?.filename || ""
        );
      const codeFrames = frames.filter((f) => /\.m?js/.test(f?.filename || ""));
      if (codeFrames.length && codeFrames.every(extensionFrame)) return null;
    }
    return event;
  },
});

// SCO-2020: @loadable/component catches a failed dynamic import internally
// (it renders its error state without rethrowing), so neither window handler
// above ever sees the rejection. Funnel chunk-load failures through
// reloadOnceForChunk before loadable swallows them.
const loadableWithReload = (importer, options) =>
  loadable(
    () =>
      importer().catch((error) => {
        reloadOnceForChunk(error);
        throw error;
      }),
    options
  );

// SCO-2966: the self check-in routes follow the `notifications` flag; remove the Legacy import with it.
const CheckinProgress = ui2027On("notifications")
  ? loadableWithReload(() => import("./pages/checkin/CheckinProgress"))
  : loadableWithReload(() => import("./pages/checkin/CheckinProgressLegacy"));
// SCO-2710: old or new page by the Profile 2027 flag; remove the Legacy imports with it.
const MainProfile = PROFILE_2027
  ? loadableWithReload(() => import("./pages/player/MainProfile"))
  : loadableWithReload(() => import("./pages/player/MainProfileLegacy"));
// SCO-3081: shared player links on the old profile page; goes with the Legacy imports.
const LegacyViewPlayer = loadableWithReload(() => import("./pages/player/components/LegacyViewPlayer"));
const OverlayWrapper = loadableWithReload(() =>
  import("./pages/streamOverlays/OverlayWrapper")
);
const FindATournament = loadableWithReload(() =>
  import("./pages/player/FindATournament")
);
// SCO-2722: old or new Seasons list by the `seasons` section flag; remove the Legacy import with it.
// A shared standing link on the old seasons page (SCO-3117): opens the player's Points Detail the new page opens itself.
const LegacySeasonPlayer = loadableWithReload(() => import("./pages/player/seasons/LegacySeasonPlayer"));
const PlayerSeasonHome = ui2027On("seasons")
  ? loadableWithReload(() => import("./pages/player/seasons/PlayerSeasonHome"))
  : loadableWithReload(() => import("./pages/player/seasons/PlayerSeasonHomeLegacy"));
// SCO-2873: old or new Find a Club by the `fac` section flag; remove the Legacy import with it.
const FindAClub = ui2027On("fac")
  ? loadableWithReload(() => import("./pages/player/FindAClub"))
  : loadableWithReload(() => import("./pages/player/FindAClubLegacy"));
const Rankings = PROFILE_2027
  ? loadableWithReload(() => import("./pages/player/Rankings"))
  : loadableWithReload(() => import("./pages/player/RankingsLegacy"));
const Practice = PROFILE_2027
  ? loadableWithReload(() => import("./pages/player/Practice"))
  : loadableWithReload(() => import("./pages/player/PracticeLegacy"));
// RAT 2027: the page's loading skeleton stands in while its code loads, so nothing appears and then moves (Phil 2026-10-08).
function RatLoadingStandIn() {
  const params = new URLSearchParams(window.location.search);
  let account = params.get("account");
  if (!account) {
    try {
      account = (JSON.parse(localStorage.getItem("organizer")) || {}).accountid || "";
    } catch (e) {
      account = "";
    }
  }
  return <RatPageSkeleton account={account} sport={window.location.pathname.split("/")[2]} />;
}
const Organizer = ui2027On("rat")
  ? loadableWithReload(() => import("./pages/organizer/Organizer"), { fallback: <RatLoadingStandIn /> })
  : loadableWithReload(() => import("./pages/organizer/OrganizerLegacy"));
const SharedCourtsPage = loadableWithReload(() =>
  import("./features/sharedCourts/SharedCourtsPage")
);
const TabletLogin = loadableWithReload(() => import("./pages/organizer/TabletLogin"));
// The old standalone players page; the 2027 Players lives inside Stats (SCO-2756).
const Players = loadableWithReload(() => import("./pages/organizer/PlayersLegacy"));
// RAT 2027 Stats (SCO-2755): StatsLegacy.jsx is the page as it was.
const Stats = ui2027On("rat")
  ? loadableWithReload(() => import("./pages/organizer/Stats"))
  : loadableWithReload(() => import("./pages/organizer/StatsLegacy"));
// RAT 2027 organizer Seasons (SCO-2757): the standalone route follows the rat flag too; SeasonsLegacy.jsx is the page as it was.
const Seasons = ui2027On("rat")
  ? loadableWithReload(() => import("./pages/organizer/Seasons"))
  : loadableWithReload(() => import("./pages/organizer/SeasonsLegacy"));
const UpgradeSPRplus = loadableWithReload(() =>
  import("./pages/GlobalModals/UpgradeSPRplus")
);
// SCO-2960: old or new vote route (61) by the `notifications` section flag; remove the Legacy import with it.
const SPRCorrectionShaming = ui2027On("notifications")
  ? loadableWithReload(() => import("./pages/Global/SPRCorrectionShaming"))
  : loadableWithReload(() => import("./pages/Global/SPRCorrectionShamingLegacy"));
const ScoutingReport = loadableWithReload(() => import("./pages/Global/ScoutingReport"));
const Shop = loadableWithReload(() => import("./pages/Global/Shop"));
const WelcomeModal = loadableWithReload(() =>
  import("./pages/GlobalModals/WelcomeModal")
);
const AdTracking = loadableWithReload(() => import("./pages/GlobalModals/AdTracking"));
const YearEndRecap = loadableWithReload(() =>
  import("./pages/GlobalModals/YearEndRecap/YearEndRecap")
);
const DashboardWrapper = loadableWithReload(() =>
  import("./pages/dashboard/DashboardWrapper")
);
const Godpower = loadableWithReload(() => import("./pages/organizer/Godpower"));
// The 2027 /qrcode page when the app's 2027 Share sheets ask for it (?look=2027: the phone's browser doesn't share the
// app's redesign switch) or this device has RAT 2027 on; else today's page.
const qrPage2027 = new URLSearchParams(window.location.search).get("look") === "2027" || ui2027On("rat");
const ShareTournamentQRCode = loadableWithReload(() =>
  qrPage2027 ? import("./pages/organizer/ShareTournamentQRCode") : import("./pages/organizer/ShareTournamentQRCodeLegacy")
);
// SCO-2960: old or new inbox by the `notifications` section flag; remove the Legacy import with it.
const Notifications = ui2027On("notifications")
  ? loadableWithReload(() => import("./pages/Global/Notifications"))
  : loadableWithReload(() => import("./pages/Global/NotificationsLegacy"));
// SCO-2943: old or new Groups page by the `groups` section flag; remove the Legacy import with it.
const GROUPS_2027 = ui2027On("groups");
const LegacyGroupLink = loadableWithReload(() => import("./pages/Global/groupFeeds/LegacyGroupLink"));
const GroupsFeedv3 = GROUPS_2027
  ? loadableWithReload(() => import("./pages/Global/groupFeeds/GroupsFeedv3"))
  : loadableWithReload(() => import("./pages/Global/groupFeeds/GroupsFeedv3Legacy"));
const AcceptPayments = loadableWithReload(() => import("./pages/Global/AcceptPayments"));
// SCO-3011: old or new home hero by the `home` section flag; remove the Legacy import with it.
const HomeEvents = ui2027On("home")
  ? loadableWithReload(() => import("./pages/Global/homeEvents/HomeEvents"))
  : loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsLegacy"));
const HomeEventsMenu = loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsMenu"));
const HomeEventsMatch = loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsMatch"));
const PlayerSettingsSheet = loadableWithReload(() => import("./pages/player/PlayerSettingsSheet"));
const NewLookSheet = loadableWithReload(() => import("./pages/Global/newLook/NewLookSheet"));
const OrganizerSettingsSheet = loadableWithReload(() => import("./pages/organizer/OrganizerSettingsSheet"));
const OrganizerQuickCreateSheet = loadableWithReload(() => import("./pages/organizer/OrganizerQuickCreateSheet"));
const HomeEventsLogin = loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsLogin"));
const HomeEventsScanIn = loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsScanIn"));
const HomeEventsRoster = loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsRoster"));
const HomeEventsShare = loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsShare"));
const HomeEventsEdit = loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsEdit"));
const HomeEventsDiscussion = loadableWithReload(() => import("./pages/Global/homeEvents/HomeEventsDiscussion"));
// Prototype: home-screen redesign handoff. Unlinked — reach via /v2/new-home only.
const NewNativeHomeScreen = loadableWithReload(() =>
  import("./pages/Global/newHome/NewNativeHomeScreen")
);
const HomeEditor = loadableWithReload(() =>
  import("./pages/Global/newHome/HomeEditor")
);
const windowLocation = window.location.href;
// ReactGA.initialize("G-3GB2H0ZENV");
ReactGA.initialize("G-1LFL9GVTFY");
// ReactGA.pageview(window.location.pathname + window.location.search);

if (process.env.NODE_ENV !== "development") console.log = () => {};

console.log(`${process.env.REACT_APP_NAME} ${process.env.REACT_APP_VERSION}`);
console.log("process.env.NODE_ENV: ", process.env.NODE_ENV);

// Module scope runs before the error boundary mounts — an unguarded parse of a
// corrupt value, or a setItem in a storage-blocked webview, is a white screen
const localStorageGlobal = readStoredJson("global");

if (!localStorageGlobal) {
  //Check if we have global settings, if not, create them
  console.log("DO WE HAVE LOCAL STORAGE???: ", localStorageGlobal);
  writeStoredJson("global", {
    darkMode: false,
    useCustomTheme: false,
    customTheme: false,
  });
}
watchSystemTheme((dark) => {
  document.body.classList.toggle("dark", dark);
  store.dispatch(setGlobal({ darkMode: dark }));
});
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Sentry.ErrorBoundary
    fallback={() => {
      // Read dark mode from localStorage at crash time so colors are correct
      // (safe-parsed: a corrupt value must not crash the crash screen)
      const isDark = readStoredJson("global", {}).darkMode;
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            textAlign: "center",
            fontFamily: "sans-serif",
            backgroundColor: isDark ? "#131A1F" : "#C0D4DE",
            color: isDark ? "#EBF1F4" : "#2D2B2D",
          }}
        >
          <p style={{ fontSize: "1.1rem", marginBottom: "1.5rem" }}>
            Something went wrong. Please reload to continue.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#CC0C0D",
              border: "1px solid #CC0C0D",
              padding: "8px 20px",
              borderRadius: "30px",
              color: "#fff",
              fontWeight: 800,
              textTransform: "uppercase",
              cursor: "pointer",
              fontSize: "0.85rem",
              letterSpacing: "0.05em",
            }}
          >
            Reload
          </button>
        </div>
      );
    }}
  >
    <Provider store={store}>
      <BrowserRouter
        basename={"/v2"}
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <EntitlementSync />
        {/* SCO-3126: the New look pop-up on the web (never inside the apps). */}
        <NewLookWebPromo />
        <Routes>
          <Route path="/" element={<App />}>
            <Route
              path=":sport/player/profile"
              element={
                <>
                  <MainProfile />
                  {!PROFILE_2027 && <LegacyViewPlayer />}
                </>
              }
            />
            <Route path=":sport/player/rankings" element={<Rankings />} />
            <Route path=":sport/player/practice" element={<Practice />} />
            <Route path=":sport/organizer" element={<Organizer />} />
            <Route path=":sport/organizer/players" element={<Players />} />
            <Route path=":sport/organizer/stats" element={<Stats />} />
            <Route path=":sport/organizer/seasons" element={<Seasons />} />
            <Route
              path="*"
              element={<Text size="2em">Page does not exist!</Text>}
            />
          </Route>
          <Route
            element={
              <NoNavLayout
                hideLogo={
                  ["create-tournament", "godpower"].some((path) =>
                    windowLocation.includes(path),
                  ) ||
                  // Seasons 2027 draws its own logo row (SCO-2724).
                  (ui2027On("seasons") && windowLocation.includes("/player/seasons")) ||
                  // FAC 2027 Discover and FAT 2027 drop the banner for their own logo row (Phil 2026-10-14: web and apps).
                  (ui2027On("fac") && windowLocation.includes("/player/findclub")) ||
                  (ui2027On("fat") && windowLocation.includes("/player/findtournament")) ||
                  // The 2027 QR page puts the Scoreholio logo under the QR instead.
                  (qrPage2027 && windowLocation.includes("/qrcode")) ||
                  // The Notifications 2027 vote page draws its own logo row (SCO-2968).
                  (ui2027On("notifications") && windowLocation.includes("/spr-correction"))
                }
              />
            }
          >
            <Route
              path=":sport/calendar/event/:gameid"
              element={<AddToCalendar />}
            />
            <Route path="/devicelogin" element={<TabletLogin />} />
            <Route path="/godpower" element={<Godpower />} />
            <Route path=":sport/qrcode" element={<ShareTournamentQRCode />} />
            <Route
              path=":sport/player/findtournament"
              element={<FindATournament />}
            />
            <Route path=":sport/player/findclub" element={<FindAClub />} />
            <Route
              path=":sport/player/seasons"
              element={
                <>
                  <PlayerSeasonHome />
                  {!ui2027On("seasons") && <LegacySeasonPlayer />}
                </>
              }
            />
            <Route path=":sport/selfcheckin" element={<CheckinProgress />} />
            <Route path="/selfcheckin" element={<CheckinProgress />} />
            <Route
              path=":sport/spr-correction"
              element={<SPRCorrectionShaming />}
            />
            <Route path=":sport/scouting-report" element={<ScoutingReport />} />
            <Route path=":sport/shop" element={<Shop />} />
            {/* Shared Courts command center — NoNavLayout so the operator grid
                gets the full width; it's a desktop tool, not a webview surface. */}
            <Route
              path=":sport/organizer/shared-courts"
              element={<SharedCourtsPage />}
            />
          </Route>
          <Route element={<UpgradeLayout />}>
            <Route path=":sport/upgradeSPRplus" element={<UpgradeSPRplus />} />
          </Route>
          <Route element={<UpgradeLayout />}>
            <Route path="modals/welcome" element={<WelcomeModal />} />
          </Route>
          <Route element={<UpgradeLayout />}>
            <Route path="modals/adtracking" element={<AdTracking />} />
          </Route>
          <Route element={<UpgradeLayout />}>
            <Route path="modals/recap" element={<YearEndRecap />} />
          </Route>
          <Route element={<BlankLayout />}>
            <Route path=":sport/home-events" element={<HomeEvents />} />
            <Route path=":sport/home-events/menu" element={<HomeEventsMenu />} />
            <Route path=":sport/home-events/match" element={<HomeEventsMatch />} />
            <Route path=":sport/home-events/login" element={<HomeEventsLogin />} />
            <Route path=":sport/home-events/scanin" element={<HomeEventsScanIn />} />
            <Route path=":sport/home-events/roster" element={<HomeEventsRoster />} />
            <Route path=":sport/home-events/share" element={<HomeEventsShare />} />
            <Route path=":sport/home-events/edit" element={<HomeEventsEdit />} />
            <Route path=":sport/home-events/discussion" element={<HomeEventsDiscussion />} />
            <Route path=":sport/player/settings" element={<PlayerSettingsSheet />} />
            <Route path=":sport/organizer/settings" element={<OrganizerSettingsSheet />} />
            <Route path=":sport/organizer/quick-create" element={<OrganizerQuickCreateSheet />} />
            {/* SCO-3067: the New look pop-up the native home opens (sportless). */}
            <Route path="modals/new-look" element={<NewLookSheet />} />
          </Route>
          <Route element={<BlankLayout />}>
            <Route path=":sport/overlay" element={<OverlayWrapper />} />
          </Route>
          {/* Prototype: customizable home + widget editor. Unlinked — reach via
              /v2/new-home (home) and /v2/new-home/edit (editor) only. */}
          <Route element={<BlankLayout />}>
            <Route path="new-home" element={<NewNativeHomeScreen />} />
            <Route path="new-home/edit" element={<HomeEditor />} />
          </Route>
          <Route element={<BlankLayout />}>
            <Route path=":sport/shop" element={<Shop />} />
          </Route>
          <Route element={<BlankLayout />}>
            <Route path="notifications" element={<Notifications />} />
            <Route
              path="groups"
              element={
                <>
                  <GroupsFeedv3 />
                  {!GROUPS_2027 && <LegacyGroupLink />}
                </>
              }
            />
            <Route path="accept-payments" element={<AcceptPayments />} />
            <Route
              path=":sport/dashboard/wanted-list"
              element={<DashboardWrapper type="wanted" />}
            />
            <Route
              path=":sport/dashboard/gameplay"
              element={<DashboardWrapper type="gameplay" />}
            />
            <Route
              path=":sport/dashboard/setup"
              element={<DashboardWrapper type="setup" />}
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </Provider>
  </Sentry.ErrorBoundary>
);

// SCO-2020: reaching this point means boot succeeded, so a prior chunk-reload
// worked — re-arm the once-only reload for the next stale deploy in this
// session. Delayed 30s so a deploy the reload did NOT fix can't turn every
// retry into another reload.
if (sessionStorage.getItem("chunkReloadAttempted")) {
  setTimeout(() => sessionStorage.removeItem("chunkReloadAttempted"), 30000);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
